
import {
    useLocation,
    useParams
} from 'react-router-dom';

import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import { LoadingOutlined, LockOutlined } from '@ant-design/icons';
import { Button as AntdButton, Input as AntdInput, Modal as AntdModal } from 'antd';
import { connect } from 'react-redux';
import { useHistory } from "react-router-dom";
import { Alert, Button as BTN, Card, CardFooter, CardHeader } from 'reactstrap';


import Box from '../components/Box';
import Button from '../components/Button';
import Check from '../components/Check';
import Drop from '../components/Drop';
import Image from '../components/Image';
import Input from '../components/Input';
import Label from '../components/Label';
import List from '../components/List';
import Radio from '../components/Radio';

import { message } from "antd";
import FormService from '../../../../redux/services/forms';
import InputTable from '../components/InputTable';
import Signature from '../components/Signature';
import Snapshot from '../components/Snapshot';
import execute_script from '../components/execute_script';
import { buildEditPayload, refreshBaseline, resetEditTracking } from './editPayload';


const buildTimestamp = (date = new Date()) => ([
    String(date.getSeconds()).padStart(2, '0'),
    String(date.getMinutes()).padStart(2, '0'),
    String(date.getHours()).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getFullYear())
]);

/**
 * The clock owns its own state so the large CreateRecord tree does not
 * re-render every second just to update the timestamp.
 *
 * timestampRef is kept current for save/create payloads without causing a
 * parent render.
 */
const RecordClock = ({ timestampRef }) => {
    const [timestamp, setTimestamp] = useState(() => {
        const initial = buildTimestamp();
        timestampRef.current = initial;
        return initial;
    });

    useEffect(() => {
        const tick = () => {
            const next = buildTimestamp();
            timestampRef.current = next;
            setTimestamp(next);
        };

        const intervalId = setInterval(tick, 1000);
        return () => clearInterval(intervalId);
    }, [timestampRef]);

    return (
        <>
            {timestamp[2]}:{timestamp[1]}:{timestamp[0]} <b>on</b>{' '}
            {timestamp[4]}/{timestamp[3]}/{timestamp[5]}
        </>
    );
};

const CreateRecord = (props) => {
    const location = useLocation();
    const history = useHistory();
    const { formId, recordId } = useParams();
    const [messageApi, contextHolder] = message.useMessage();
    // const navigate = useNavigate();

    const [showLargeRecord, setShowLargeRecord] = useState(false);

    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dbId, setDbId] = useState('');

    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [inputPassword, setInputPassword] = useState('');

    const mode = (props?.user?.is_superuser || props.user.groups.indexOf("Branch Admin") > -1) ? "admin" : "user"; // "admin"

    const [allowedPages, setAllowedPages] = useState([]); // for visibility
    const [editablePages, setEditablePages] = useState([]); // for editability
    const [selfEditablePages, setSelfEditablePages] = useState([]); // for editability


    const [recordOwner, setRecordOwner] = useState(-1);

    const [formName, setFormName] = useState('My Form');
    const [name, setName] = useState('New Record');
    const [editingName, setEditingName] = useState(false);

    const [errorDialog, setErrorDialog] = useState(false);


    // Array of components to be rendered
    const [components, setComponents] = useState([]);

    // States for pages
    const [pages, setPages] = useState([]);

    const [currentPage, setCurrentPage] = useState(0);
    const [pageReady, setPageReady] = useState(false);

    // pages the user actually touched while editing this record
    const [editedPages, setEditedPages] = useState([]);

    const markPageEdited = useCallback((page) => {
        if (page === undefined || page === null) {
            return;
        }

        setEditedPages((prev) => (
            prev.includes(page) ? prev : [...prev, page]
        ));
    }, []);

    // Array positions of every component the user directly edited.
    const editedComponents = useRef(new Set());

    // Per-component pre-edit baseline.
    const baselines = useRef(new Map());

    /**
     * Runtime widgets already call setter(newComponents), so keep that API.
     *
     * The setter is cached per component index, which means children do not
     * receive a brand-new setter function on every CreateRecord render.
     *
     * Event-index 1 scripts are triggered from here, after an actual widget
     * edit, instead of being triggered from the render path.
     */
    const componentSetterCache = useRef(new Map());
    const changeScriptRunnerRef = useRef(null);

    const makeSetter = useCallback((componentIndex) => {
        if (!componentSetterCache.current.has(componentIndex)) {
            componentSetterCache.current.set(componentIndex, (newComponents) => {
                editedComponents.current.add(componentIndex);

                const page = newComponents?.[componentIndex]?.page;
                markPageEdited(page);

                setComponents(newComponents);
                
                if (changeScriptRunnerRef.current) {
                    changeScriptRunnerRef.current(newComponents);
                }
            });
        }

        return componentSetterCache.current.get(componentIndex);
    }, [markPageEdited]);

    // VARS
    const [vars, setVars] = useState([]);
    const [initialVars, setInitialVars] = useState([]);     // stores the initial value of `vars` to compare later -- used to only send edited `vars` to server

    // EXTERNALS
    const [externals, setExternals] = useState({
        flags: [
            {
                name: "Loading Flags...",
                values: ['Loading Flags...'],
                colors: ['#fff'],
                value: 0
            }
        ]
    });
    const [initialExternals, setInitialExternals] = useState({
        flags: [
            {
                name: "Loading Flags...",
                values: ['Loading Flags...'],
                colors: ['#fff'],
                value: 0
            }
        ]
    });                                                     // stores the initial value of `externals` to compare later -- used to only send edited `externals` to server

    const [isEditingFlags, setIsEditingFlags] = useState(false);
    const [currentFlag, setCurrentFlag] = useState(0);
    const [viewCurrentFlag, setViewCurrentFlag] = useState(false);
    const message_key = 'updatable';

    // an array of true/false values indicating whether a component's script should run on start
    // is useful for restricting the execution of start scripts from components with loops
    const [shouldRunElementOnStart, setShouldRunElementOnStart] = useState([]);

    // Latest mutable values used by the script runner. Updating these refs
    // does not cause a render.
    const shouldRunElementOnStartRef = useRef([]);
    const varsRef = useRef(vars);
    const externalsRef = useRef(externals);
    const recordNameRef = useRef(name);

    varsRef.current = vars;
    externalsRef.current = externals;
    recordNameRef.current = name;

    const saveButton = useRef();
    // handle what happens on key press
    const handleKeyPress = useCallback((event) => {
        if ((event.ctrlKey === true || event.metaKey === true) && event.key == 'Enter') {
            saveButton.current.click();
        }
        // if (event.shiftKey === true && event.key == 'S') {
        //     saveButton.current.click();
        // }
    }, []);
    useEffect(() => {
        // attach the event listener
        document.addEventListener('keydown', handleKeyPress);

        // remove the event listener
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, []);

    const updateRecord = (closePage) => {
        if (recordId == null || recordId == undefined || recordId == '') {
            return;
        }
        messageApi.open({
            key: message_key,
            type: 'loading',
            content: 'Saving...',
        });

        // send only what the user edited: per-component when every component
        // has a creation_index, per-page otherwise (see editPayload.js)
        const editPayload = buildEditPayload(components, editedComponents.current, baselines.current, editedPages);

        // compute `vars` payload, only send those variables which have been edited
        let payloadVars = [];
        for (let k = 0; k < vars.length; k++) {
            if (Object.hasOwn(initialVars[k], "name") && Object.hasOwn(initialVars[k], "value") && Object.hasOwn(vars[k], "name") && Object.hasOwn(vars[k], "value")) {
                if (initialVars[k]['name'] === vars[k]['name']) {
                    if (initialVars[k]['value'] !== vars[k]['value']) {
                        payloadVars.push(vars[k]);
                    }
                }
            }
        }

        // compute `externals` payload, only send those externals which have been edited
        let payloadExternals = {
            flags: []
        };

        if (Object.hasOwn(externals, "creation_index")) {
            payloadExternals['creation_index'] = externals['creation_index'];
        }

        if (Object.hasOwn(externals, 'flags') && Object.hasOwn(initialExternals, 'flags')) {
            for (let k = 0; k < externals['flags'].length; k++) {
                if (Object.hasOwn(externals['flags'][k], 'name') && Object.hasOwn(externals['flags'][k], 'value') && Object.hasOwn(initialExternals['flags'][k], 'name') && Object.hasOwn(initialExternals['flags'][k], 'value')) {
                    if (externals['flags'][k]['name'] === initialExternals['flags'][k]['name']) {
                        if (externals['flags'][k]['value'] !== initialExternals['flags'][k]['value']) {
                            payloadExternals['flags'].push(externals['flags'][k]);
                        }
                    }
                }
            }
        }

        let actualPayload = {
            name: name,
            datetime: timestampRef.current,
            form: formId,
            requestType: editPayload.requestType,
            // editedPages: editPayload.editedPages,
            content: editPayload.requestType === 'record' ? components : editPayload.content,
            pages: pages,
            vars: payloadVars,
            externals: payloadExternals,
            auth: auth
        };

        if (editPayload.requestType !== 'record') {
            actualPayload['editedPages'] = editPayload.editedPages;
        }

        FormService.updateRecord(
            recordId,
            actualPayload
        ).then(res => {
            messageApi.open({
                key: message_key,
                type: 'success',
                content: 'Record saved successfully.',
            });
            // the server now matches what was just sent — make the saved
            // state the new pre-edit reference, so a later revert of an
            // already-saved edit is detected as a change and sent (this save
            // can keep the record open, e.g. via Ctrl/Cmd+Enter)
            resetEditTracking(components, editedComponents.current, baselines.current);
            setLoading(false);
            if (closePage) {
                history.push(`/forms/${formId}/records`);
            }
        }).catch(res => {
            messageApi.open({
                key: message_key,
                type: 'error',
                content: 'Your record could not be saved.',
            });
            setLoading(false);
        });
    }



    let render_flags = [];
    let render_flag = [];
    try {
        for (let k = 0; k < externals.flags.length; ++k) {
            render_flags.push(
                <a key={`flag_${k}`} style={{
                    width: '90%',
                    fontWeight: '300',
                    padding: '0.5vmax 0',
                    border: '1pt #faf3dd solid',
                    borderTop: k !== 0 ? '0pt' : '1pt #faf3dd solid'
                }} className='panel-button' onClick={(e) => {
                    e.preventDefault();
                    setCurrentFlag(k);
                    setViewCurrentFlag(true);
                }}>
                    {externals.flags[k].name}
                </a>
            );
        }
        for (let k = 0; k < externals.flags[currentFlag].values.length; ++k) {
            render_flag.push(
                <a key={`flag_value_${k}`} style={{
                    width: '90%',
                    fontWeight: '300',
                    padding: '0.5vmax 0',
                    border: '1pt #faf3dd solid',
                    borderTop: k !== 0 ? '0pt' : '1pt #faf3dd solid',
                    backgroundColor: parseInt(externals.flags[currentFlag].value) === k ? '#faf3dd' : '#000',
                    color: parseInt(externals.flags[currentFlag].value) === k ? '#000' : '#faf3dd'
                }} className='panel-button' onClick={(e) => {
                    e.preventDefault();
                    let new_externals = { ...externals };
                    new_externals.flags[currentFlag].value = k;
                    setExternals(new_externals);
                }}>
                    {externals.flags[currentFlag].values[k]}
                </a>
            );
        }
    } catch (err) {
    }

    const [isSaving, setIsSaving] = useState(false);
    const [auth, setAuth] = useState({
        password: ''
    });

    const timestampRef = useRef(buildTimestamp());

    const getFormPages = async (record) => {
        const res = await FormService.getFormPages(formId);
        setRecordOwner(record.created_by);
        setName(record.name);
        setAuth(record.auth);
        setComponents(record.content);
        // if (record.content.length >= 2000) {
        //     setShowLargeRecord(true);
        // }
        setExternals(record.externals);
        setPages(record.pages);
        setVars(record.vars);
        setInitialVars(JSON.parse(JSON.stringify(record.vars)));
        setInitialExternals(JSON.parse(JSON.stringify(record.externals)));
        setLoaded(true);
        setLoading(false);
        setPageReady(true);
        setEditablePages(res.editable_pages);
        setSelfEditablePages(res.self_editable_pages);
        setAllowedPages(res.allowed_pages);

        // let array1 = res.editable_pages.concat(res.self_editable_pages);
        // setEditablePages(array1);

        // FormService.getFormPages(formId).then(res => {
        //     setRecordOwner(record.created_by);
        //     setName(record.name);
        //     setAuth(record.auth);
        //     setComponents(record.content);
        //     setExternals(record.externals);
        //     setPages(record.pages);
        //     setVars(record.vars);

        //     setLoaded(true);
        //     setLoading(false);
        //     setPageReady(true);
        //     setEditablePages(res.editable_pages);
        //     setSelfEditablePages(res.self_editable_pages);
        //     setAllowedPages(res.allowed_pages);

        //     // let array1 = res.editable_pages.concat(res.self_editable_pages);
        //     // setEditablePages(array1);

        //     window.setTimeout(() => {
        //         setComponentsHaveRendered(false);
        //     }, 1000);
        // });
    }

    const [scriptType, setScriptType] = useState('All');
    const [scriptDelay, setScriptDelay] = useState(1000);

    useEffect(() => {
        setLoading(true);
        if (recordId != null && recordId != undefined) {
            FormService.getRecord(recordId).then(res => {
                setScriptType(res.record.script_type);
                setScriptDelay(res.record.script_delay);
                if (res.record.auth.password != "" && props?.user.groups.includes('Branch Admin') == false) {
                    setPasswordModalVisible(true);
                }
                getFormPages(res.record).then(() => {
                    setLoading(false);
                });
            });
            

            // .catch(err => {
            //     if (err.request.status != 200) {
            //         alert('Record not available');
            //         history.push('/forms');
            //     }
            // });
        }
        else {
            FormService.getFormDetail(formId).then(form => {
                setScriptType(form.script_type);
                setScriptDelay(form.script_delay);
                setLoading(true);
                setName(form?.name + ": New Record");
                FormService.getFormPages(formId).then(res => {
                    const _pages = []
                    const _contents = [];

                    res.data.map(item => {
                        item["page"]["page_id"] = item.page_id;
                        _pages.push(item["page"]);

                        item.content.map(content => {
                            content["page_id"] = item.page_id;
                            _contents.push(content);
                        });
                    });

                    setFormName(form.name);
                    setVars(form.vars);
                    setExternals(form.externals);
                    setDbId(form.form_id);
                    setComponents(_contents);
                    // if (_contents.length >= 2000) {
                    //     setShowLargeRecord(true);
                    // }
                    setPages(_pages);
                    setLoading(false);
                    setLoaded(true);
                    setPageReady(true);

                    setInitialVars(JSON.parse(JSON.stringify(form.vars)));
                    setInitialExternals(JSON.parse(JSON.stringify(form.externals)));

                    setAllowedPages(res.allowed_pages);
                    let array1 = res.editable_pages.concat(res.self_editable_pages);
                    setEditablePages(array1);
                }).catch(err => {
                    setLoading(false);
                });
            }).catch(err => {
                if (err.request.status != 200) {
                    alert('Form not available');
                    history.push('/forms');
                }
                setLoading(false);
            });
        }



    }, []);



    const startupScriptsExecutedRef = useRef(false);

    /**
     * Run event-index 0 exactly once after the record/form has been loaded.
     * Previously this lived in the rendering effect and could run again when
     * the page changed.
     */
    useEffect(() => {
        if (!loaded || !pageReady || startupScriptsExecutedRef.current) {
            return;
        }

        const scriptFlags = components.map((component) => {
            if (!Object.hasOwn(component, 'script')) {
                return false;
            }

            const firstScript = component.script?.[0];
            const serializedScript = JSON.stringify(firstScript) ?? '';

            // Preserve the original loop/range protection.
            return !serializedScript.includes('loop/range');
        });

        shouldRunElementOnStartRef.current = scriptFlags;
        setShouldRunElementOnStart(scriptFlags);
        startupScriptsExecutedRef.current = true;

        // if (components.length < 2000) {
            console.log("RUNNING ONSTART SCRIPTS");

            for (let j = 0; j < components.length; ++j) {
                console.log("counter > ", j);
                execute_script(
                    0,
                    j,
                    components,
                    setComponents,
                    varsRef.current,
                    setVars,
                    externalsRef.current,
                    setExternals,
                    recordNameRef.current,
                    setName
                );
            }
        // }
        // Intentionally keyed to load readiness. execute_script may itself
        // update components, so components must not be a dependency here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded, pageReady]);

    /**
     * Run event-index 1 only after a runtime widget actually changes the
     * components array. No scroll handler, timestamp tick, page render, or
     * unrelated state change can trigger these scripts anymore.
     */
    const runScriptTimerRef = useRef(null);
    const pendingComponentsRef = useRef(null);

    const runChangeScripts = useCallback((nextComponents) => {
        if (!Array.isArray(nextComponents)) {
            return;
        }
        const scriptFlags = shouldRunElementOnStartRef.current;
        // Always keep latest component state
        pendingComponentsRef.current = nextComponents;

        // Cancel previous debounce
        if (runScriptTimerRef.current !== null) {
            window.clearTimeout(runScriptTimerRef.current);
        }
        runScript(nextComponents, scriptFlags);
    }, []);

    const runScript = (nextComponents, scriptFlags)=>{
        // Need to add 
        let currentPageComponents = nextComponents;
        if(scriptType!='All'){
            currentPageComponents = nextComponents.filter(
                component => component.page === (currentPage+1)
            );
        }
        for (let j = 0; j < currentPageComponents.length; ++j) {
            console.log("counter > ", j);
            if (scriptFlags[j]) {
                execute_script(
                    1,
                    j,
                    nextComponents,
                    setComponents,
                    varsRef.current,
                    setVars,
                    externalsRef.current,
                    setExternals,
                    recordNameRef.current,
                    setName
                );
            }
        }
    }

    // Stable setters call the latest change-script runner through this ref.
    changeScriptRunnerRef.current = runChangeScripts;

    /**
     * Keep the baseline updated until a component is first touched.
     * This mutates a ref only; it does not trigger another React render.
     */
    useEffect(() => {
        for (let k = 0; k < components.length; ++k) {
            if (components[k].page === currentPage) {
                refreshBaseline(
                    baselines.current,
                    editedComponents.current,
                    k,
                    components[k]
                );
            }
        }
    }, [components, currentPage]);

    /**
     * Validation messages are derived data. Do not store them in state and
     * cause a second render.
     */
    const { errorsRender, warningsRender } = useMemo(() => {
        let newErrors = "";
        let newWarnings = "";

        for (let k = 0; k < components.length; ++k) {
            const component = components[k];

            const canEditComponent =
                editablePages.includes(component.page) ||
                (
                    selfEditablePages.includes(component.page) &&
                    recordOwner?.id === props.user.id
                ) ||
                mode === "admin";

            if (!canEditComponent) {
                continue;
            }

            if (component.type !== 'intermediate/InputTable') {
                if (component.error !== "") {
                    newErrors =
                        `${newErrors}\nPage: ${component.page + 1}, ` +
                        `${component.name} (${k}): ${component.error}`;
                }

                if (Object.hasOwn(component, 'warning') && component.warning) {
                    newWarnings =
                        `${newWarnings}\n${component.name} (${k}): ` +
                        `${component.warning}`;
                }

                continue;
            }

            const rowCount = Array.isArray(component.data)
                ? component.data.length
                : 0;

            for (let p = 0; p < rowCount; ++p) {
                const columnCount = Array.isArray(component.data[p])
                    ? component.data[p].length
                    : 0;

                for (let q = 0; q < columnCount; ++q) {
                    const error = component.error?.[p]?.[q] ?? "";
                    const warning = component.warning?.[p]?.[q] ?? "";

                    if (error !== "") {
                        newErrors =
                            `${newErrors}\nPage: ${component.page + 1}, ` +
                            `${component.name} (Row ${p + 1}, ` +
                            `Column ${q + 1}): ${error}`;
                    }

                    if (warning !== "") {
                        newWarnings =
                            `${newWarnings}\nPage: ${component.page + 1}, ` +
                            `${component.name} (Row ${p + 1}, ` +
                            `Column ${q + 1}): ${warning}`;
                    }
                }
            }
        }

        return {
            errorsRender: newErrors,
            warningsRender: newWarnings
        };
    }, [
        components,
        editablePages,
        selfEditablePages,
        recordOwner,
        props.user.id,
        mode
    ]);

    /**
     * Build the current page's JSX as derived data. It is never put back into
     * React state, so there is no setComponentsRender -> second-render cycle.
     */
    const componentsRender = useMemo(() => {
        if (!(allowedPages.includes(currentPage) || mode === "admin")) {
            return [
                <div
                    key="access-prohibited"
                    style={{
                        width: '100%',
                        height: '20vh',
                        fontSize: '1.25vmax',
                        textAlign: 'center',
                        lineHeight: '20vh',
                        color: 'black'
                    }}
                >
                    Access Prohibited
                </div>
            ];
        }

        const result = [];
        const viewportWidth = 0.95 * window.innerWidth;

        for (let k = 0; k < components.length; ++k) {
            const component = components[k];

            if (component.page !== currentPage) {
                continue;
            }

            const editable =
                editablePages.includes(currentPage) ||
                (
                    selfEditablePages.includes(component.page) &&
                    recordOwner?.id === props.user.id
                ) ||
                mode === 'admin';

            const commonProps = {
                id: k,
                comps: components,
                setter: makeSetter(k),
                vw: viewportWidth,
                vars,
                setVars,
                editable,
                externals,
                setExternals,
                recordName: name,
                setRecordName: setName
            };

            // Index is deliberately part of the key because this codebase
            // explicitly supports duplicated legacy creation_index values.
            const key = `${component.type}_${k}`;

            switch (component.type) {
                case 'primitive/Box':
                    result.push(<Box key={key} {...commonProps} />);
                    break;

                case 'primitive/Label':
                    result.push(<Label key={key} {...commonProps} />);
                    break;

                case 'primitive/Button':
                    result.push(<Button key={key} {...commonProps} />);
                    break;

                case 'primitive/Input':
                    result.push(<Input key={key} {...commonProps} />);
                    break;

                case 'primitive/List':
                    result.push(<List key={key} {...commonProps} />);
                    break;

                case 'primitive/Check':
                    result.push(<Check key={key} {...commonProps} />);
                    break;

                case 'primitive/Radio':
                    result.push(<Radio key={key} {...commonProps} />);
                    break;

                case 'primitive/Drop':
                    result.push(<Drop key={key} {...commonProps} />);
                    break;

                case 'primitive/Image':
                    result.push(<Image key={key} {...commonProps} />);
                    break;

                case 'intermediate/Signature':
                    result.push(<Signature key={key} {...commonProps} />);
                    break;

                case 'intermediate/InputTable':
                    result.push(
                        <InputTable
                            key={key}
                            {...commonProps}
                            currentPage={currentPage}
                        />
                    );
                    break;

                case 'intermediate/Snapshot':
                    result.push(<Snapshot key={key} {...commonProps} />);
                    break;

                default:
                    console.warn(
                        `Unsupported component type "${component.type}" at index ${k}`
                    );
                    break;
            }
        }

        return result;
    }, [
        allowedPages,
        currentPage,
        mode,
        components,
        editablePages,
        selfEditablePages,
        recordOwner,
        props.user.id,
        makeSetter,
        vars,
        externals,
        name
    ]);


    return (
        <div className='suman123'>
            {
                ((editablePages.includes(currentPage) == false && (selfEditablePages.includes(currentPage) == false && recordOwner.id != props.user.id) && mode != "admin")) ? (
                    <div style={{
                        'display': 'block',
                        'background': 'orange',
                        'font-weight': 'bold',
                        'text-align': 'center',
                        'color': '#fff'
                    }} > You don't have permission on this page.</div>
                ) : (
                    null
                )
            }

            {contextHolder}
            {
                loading ? (
                    <div className="blockUI">
                        <div className="loading-icon-container">
                            <LoadingOutlined />
                            <br />
                            <br />
                            <strong>Loading...</strong>
                        </div>
                    </div>
                ) : null
            }

            <AntdModal title={"Unlock This Record"} visible={passwordModalVisible} footer={[
                <AntdButton type="primary" onClick={(e) => {
                    if (inputPassword == auth.password) {
                        setPasswordModalVisible(false);
                    }
                    else {
                        alert('Invalid Password!');
                    }
                }}>Unlock</AntdButton>,
                <AntdButton type="danger" onClick={(e) => {
                    history.push(`/forms/${formId}/records`);
                }}>Close</AntdButton>
            ]}>
                <AntdInput placeholder='Enter your password' onChange={(e) => {
                    setInputPassword(e.target.value);
                }} prefix={<LockOutlined />} />
            </AntdModal>


            <Card style={{ width: '98%', margin: '10px auto' }}>
                <CardHeader>
                    <strong>{formName}</strong>

                    <span style={{ float: 'right' }}>
                        <b>Timestamp:</b> <RecordClock timestampRef={timestampRef} />
                    </span>
                </CardHeader>
            </Card>
            {/* <CardBody> */}
            <div style={{ marginTop: 20 }}>
                <div id="wrapper" style={{
                    position: 'absolute',
                    top: '135px',
                    left: '0',
                    width: '100%',
                    // margin: '0px auto',
                    // marginBottom: 100
                    height: '80vh',
                    // backgroundColor: '#fff',
                    overflow: 'auto',

                }}>
                    {
                        loaded ? <div className='abc' onChange={(e) => {
                            markPageEdited(currentPage);
                        }} style={{
                            // background: '#ccc',
                            width: '95vw',
                            marginLeft: '2.5vw',
                            // width: pages[currentPage].width + "vw",
                            backgroundColor: '#fff',
                            height: pages?.length > 0 && pages[currentPage]?.height ? (pages[currentPage]?.height * 95).toString() + 'vw' : '30vw',
                            // height: (pages[currentPage].height * 95).toString() + "vw",
                            marginBottom: 150
                        }}>

                            {
                                componentsRender?.map((item, index) => {
                                    return <span key={`rendered_element_${index}`} title={`Element ${index}`}>
                                        {item}
                                    </span>
                                })
                            }
                        </div> : (
                            <div>Loading...</div>
                        )
                    }
                </div>
            </div>
            {/* </CardBody> */}
            <Card>
                <CardFooter>
                    <AntdModal title="Errors" visible={errorDialog} onOk={(e) => {
                        setErrorDialog(false)
                    }} onCancel={(e) => {
                        setErrorDialog(false)
                    }}>
                        <div>
                            {
                                errorsRender !== "" ? (
                                    <Alert color="danger" style={{ whiteSpace: "pre-wrap" }}>
                                        {errorsRender}
                                    </Alert>
                                ) : null
                            }
                            {
                                warningsRender !== "" ? (
                                    <Alert color="warning" style={{ whiteSpace: "pre-wrap" }}>
                                        <strong>Warning:</strong>
                                        {warningsRender}
                                    </Alert>
                                ) : null
                            }
                        </div>
                    </AntdModal>

                    <div style={{ position: 'fixed', bottom: 10, width: '100%' }}>
                        {/* {
                            errorsRender !== "" ? (
                                <Alert color="danger">
                                    <strong>Error:</strong>
                                    {errorsRender}
                                </Alert>
                            ) : null
                        }
                        {
                            warningsRender !== "" ? (
                                <Alert color="warning">
                                    <strong>Warning:</strong>
                                    {warningsRender}
                                </Alert>
                            ) : null
                        } */}

                        <Alert color="primary" style={{ marginBottom: 0 }}>
                            {/* <div>
                                {
                                    (errorsRender !== "" || warningsRender !== "") ? (
                                        <BTN color="danger" size="sm" onClick={(e) => {
                                            e.preventDefault();
                                            setErrorDialog(true);
                                        }}>
                                            This Page has some error(s). Click here to see.
                                        </BTN>
                                    ) : null
                                }
                            </div> */}
                            <div style={{ textAlign: 'center' }}>
                                <i className="fa fa-solid fa-chevron-left panel-icon" style={{
                                    fontSize: '1vmax',
                                    marginRight: '5vmax',
                                    padding: '0.45vmax 0.6vmax',
                                    borderRadius: '100px'
                                }} onClick={(e) => {
                                    if (currentPage !== 0) {
                                        setCurrentPage(currentPage - 1);
                                    } else {
                                        setCurrentPage(pages.length - 1);
                                    }
                                }}></i>
                                Page {currentPage + 1}/{pages.length}
                                <i className="fa fa-solid fa-chevron-right panel-icon" style={{
                                    fontSize: '1vmax',
                                    padding: '0.45vmax 0.6vmax',
                                    marginLeft: '5vmax',
                                    borderRadius: '100px'
                                }} onClick={(e) => {
                                    if (currentPage !== pages.length - 1) {
                                        setCurrentPage(currentPage + 1)
                                    } else {
                                        setCurrentPage(0);
                                    }
                                }}></i>
                            </div>

                            <div style={{ position: 'absolute', top: 15, right: 15 }}>
                                {
                                    (warningsRender !== "" && errorsRender === "") ? (
                                        <BTN color="warning" size="sm" onClick={(e) => {
                                            e.preventDefault();
                                            setErrorDialog(true);
                                        }}>
                                            This Page has some warning(s). Click here to see.
                                        </BTN>
                                    ) : (
                                        <></>
                                    )
                                }

                                {
                                    (errorsRender !== "") ? (
                                        <BTN color={errorsRender !== "" ? "danger" : "warning"} size="sm" onClick={(e) => {
                                            e.preventDefault();
                                            setErrorDialog(true);
                                        }}>
                                            This Page has some error(s)/warning(s). Click here to see.
                                        </BTN>
                                    ) : (

                                        location?.state?.mode != 'view' ? (
                                            <>
                                                {/* {components.length >= 2000 ? <><BTN color="primary" size="sm" onClick={(e) => {
                                                    e.preventDefault();
                                                    for (let j = 0; j < components.length; ++j) {
                                                        if (shouldRunElementOnStart[j]) {
                                                            execute_script(1, j, components, setComponents, vars, setVars, externals, setExternals, name, setName);
                                                        }
                                                    }
                                                }}
                                                    disabled={components.length < 2000}
                                                >
                                                    <i class="fa fa-play" aria-hidden="true" style={{
                                                        marginRight: '5px'
                                                    }}></i>
                                                    Run All Scripts
                                                </BTN>
                                                &nbsp;&nbsp;</> : <></>} */}

                                                <BTN color="primary" size="sm" onClick={(e) => {
                                                    e.preventDefault();
                                                    if (editablePages.length > 0 || selfEditablePages.length > 0 || mode === "admin") {
                                                        setIsEditingFlags(true);
                                                    }
                                                }}
                                                    disabled={editablePages.length <= 0 && selfEditablePages.length <= 0 && mode != "admin"}
                                                >
                                                    Edit Flags
                                                </BTN>
                                                &nbsp;&nbsp;
                                                <BTN color="primary" size="sm" disabled={editablePages.length <= 0 && selfEditablePages.length <= 0 && mode != "admin"} onClick={(e) => {
                                                    e.preventDefault();
                                                    
                                                    // Run all script
                                                    for (let j = 0; j < components.length; ++j) {
                                                        if (shouldRunElementOnStart[j]) {
                                                            execute_script(1, j, components, setComponents, vars, setVars, externals, setExternals, name, setName);
                                                        }
                                                    }


                                                    if (editablePages.length > 0 || selfEditablePages.length > 0 || mode === "admin") {
                                                        setIsSaving(true);
                                                    }
                                                }}>
                                                    Save
                                                </BTN>
                                                <button ref={saveButton} style={{ display: 'none' }}
                                                    disabled={editablePages.length <= 0 && selfEditablePages.length <= 0 && mode != "admin"}
                                                    onClick={() => {
                                                        // Run all script
                                                        for (let j = 0; j < components.length; ++j) {
                                                            if (shouldRunElementOnStart[j]) {
                                                                execute_script(1, j, components, setComponents, vars, setVars, externals, setExternals, name, setName);
                                                            }
                                                        }

                                                        
                                                        if (editablePages.length > 0 || selfEditablePages.length > 0 || mode === "admin") {

                                                            updateRecord(false);
                                                        }
                                                    }
                                                    }>Save</button>
                                            </>
                                        ) : null
                                    )
                                }

                                &nbsp;&nbsp;
                                <BTN color="danger" size="sm" onClick={(e) => {
                                    e.preventDefault();
                                    if (recordId != null && recordId != undefined) {
                                        // history.push(`/forms/${formId}/records`);
                                        history.goBack();
                                    }
                                    else {
                                        history.push(`/forms`);
                                    }

                                }}>
                                    Cancel
                                </BTN>

                            </div>
                        </Alert>
                    </div>
                </CardFooter >

            </Card >

            {/* Modal to indicate to the user that this form is large, so will be treated differently! */}
            {
                <AntdModal visible={showLargeRecord} title="This is a Large Record" onOk={() => {
                    setShowLargeRecord(false);
                }} onCancel={() => {
                    setShowLargeRecord(false);
                }}>
                    To save time, <strong>scripts will not run on their own until you run them.</strong> To do this, please press the "Run All Scripts" button on the bottom-right.
                </AntdModal>
            }


            {
                isSaving && errorsRender === "" ? <>

                    <AntdModal visible={true} title="Save Record" onOk={() => {
                        if (loaded) {
                            setLoading(true);
                            for (let j = 0; j < components.length; ++j) {
                                execute_script(2, j, components, setComponents, vars, setVars, externals, setExternals, name, setName);
                            }

                            if (recordId != null && recordId != undefined) {
                                updateRecord(true);
                            }
                            else {
                                FormService.createRecord({
                                    name: name,
                                    datetime: timestampRef.current,
                                    script_delay: scriptDelay,
                                    script_type: scriptType,
                                    form: formId,
                                    // creator: id,
                                    content: components,
                                    pages: pages,
                                    vars: vars,
                                    externals: externals,
                                    auth: auth
                                }).then(res => {
                                    setLoading(false);
                                    // history.push('/forms');
                                    history.push(`/forms/${formId}/records`);
                                }).catch(res => {
                                    setLoading(false);
                                })
                            }
                        }
                    }} onCancel={() => {
                        setIsSaving(false);
                    }}>

                        <p style={{
                            fontWeight: '300',
                            fontSize: '1vmax'
                        }}>
                            Record Name:

                            <input type="text" value={name} className='panel-input' onChange={(e) => {
                                setName(e.target.value);
                            }} />

                        </p>
                        <p style={{
                            fontWeight: '300',
                            fontSize: '1vmax'
                        }}>Timestamp: <b><RecordClock timestampRef={timestampRef} /></b></p>
                        <p style={{
                            fontWeight: '300',
                            fontSize: '1vmax',
                            display: 'inline-block',
                            margin: '0',
                            marginBottom: '1.5vmax'
                        }}>Password: </p><input className='panel-input' style={{
                            margin: '0',
                            marginLeft: '0.5vmax'
                        }} placeholder='No Password' type={props?.user.groups.includes('Branch Admin') ? "text" : "password"} value={auth.password} onChange={(e) => {
                            setAuth({
                                password: e.target.value
                            });
                        }}></input>
                        {/* <br />
                        <a className='panel-button' style={{
                            marginRight: '1vmax'
                        }} onClick={(e) => {
                            e.preventDefault();

                            if (loaded) {
                                setLoading(true);
                                for (let j = 0; j < components.length; ++j) {
                                    execute_script(2, j, components, setComponents, vars, setVars, externals, setExternals, name, setName);
                                }

                                if (recordId != null && recordId != undefined) {
                                    updateRecord(true);
                                }
                                else {
                                    FormService.createRecord({
                                        name: name,
                                        datetime: timestampRef.current,
                                        form: formId,
                                        // creator: id,
                                        content: components,
                                        pages: pages,
                                        vars: vars,
                                        externals: externals,
                                        auth: auth
                                    }).then(res => {
                                        setLoading(false);
                                        // history.push('/forms');
                                        history.push(`/forms/${formId}/records`);
                                    }).catch(res => {
                                        setLoading(false);
                                    })
                                }
                            }
                        }}>Save</a>
                        <a className='panel-button' onClick={(e) => {
                            e.preventDefault();
                            setIsSaving(false);
                        }}>Cancel</a> */}
                    </AntdModal>
                </> :

                    <>
                    </>
            }
            {
                isEditingFlags ? <AntdModal visible={true} title="Edit Flags" onOk={() => { setIsEditingFlags(false); }}
                    onCancel={() => { setIsEditingFlags(false); }}>

                    <div style={{
                        height: '65%',
                        overflow: 'scroll'
                    }}>
                        {render_flags}
                    </div>
                    {/* <div style={{
                        position: 'absolute',
                        bottom: '0',
                        width: '100%',
                        backgroundColor: '#000000'
                    }}>
                        <a href='' className='panel-button' style={{
                            marginBottom: '1vmax'
                        }} onClick={(e) => {
                            e.preventDefault();
                            setIsEditingFlags(false);
                        }}>Okay</a>
                    </div> */}
                </AntdModal> : <></>
            }
            {
                viewCurrentFlag && loaded ? <AntdModal visible={true} title={`Edit ${externals.flags[currentFlag].name}`} onOk={() => { setViewCurrentFlag(false); }}
                    onCancel={() => { setViewCurrentFlag(false); }}>

                    <div style={{
                        height: '65%',
                        overflow: 'scroll'
                    }}>
                        {render_flag}
                    </div>
                    {/* <div style={{
                        position: 'absolute',
                        bottom: '0',
                        width: '100%',
                        backgroundColor: '#000000'
                    }}>
                        <a href='' className='panel-button' style={{
                            marginBottom: '1vmax'
                        }} onClick={(e) => {
                            e.preventDefault();
                            setViewCurrentFlag(false);
                        }}>Okay</a>
                    </div> */}
                </AntdModal> : <></>
            }
        </div >
    );

}

function mapStateToProps(state) {
    const { user } = state.auth;
    return {
        user
    }
}
export default connect(mapStateToProps)(CreateRecord);